import { apiRequest, ApiError } from './client'

export { ApiError }

// TTL options matching backend
export const TTL_OPTIONS = {
  '1h': '1 hour',
  '12h': '12 hours',
  '1d': '1 day',
  '3d': '3 days',
  '7d': '7 days',
  '30d': '30 days',
  '90d': '90 days',
  '180d': '180 days',
  '1y': '1 year',
  never: 'Never',
} as const

export type TTLOption = keyof typeof TTL_OPTIONS

export interface ApiKeyResponse {
  apiKey: string
  name: string | null
  createdAt: number
  expiresAt: number | null
  message: string
}

export interface UsageInfo {
  activeShares: number
  maxShares: number
  storageUsed: number
  maxStorage: number
  tier: 'free' | 'starter' | 'pro'
}

export interface RateLimitInfo {
  minuteUsed: number
  minuteMax: number
  minuteResetAt: number
  dayUsed: number
  dayMax: number
  dayResetAt: number
}

export interface ApiKeyStats {
  valid: boolean
  name?: string | null
  shareCount?: number
  tier?: 'free' | 'starter' | 'pro'
  usage?: UsageInfo
  rateLimit?: RateLimitInfo
  createdAt?: number
  expiresAt?: number | null
  lastUsedAt?: number | null
  reason?: 'invalid' | 'expired' | 'revoked'
}

export interface ShareSummary {
  id: string
  filename: string | null
  cipher: string
  isModifiable: boolean
  createdAt: number
  expiresAt: number
  burnAfterRead: boolean
  consumedAt: number | null
  revokedAt: number | null
  viewCount: number
  status: 'active' | 'revoked' | 'consumed' | 'expired'
  wrappedPasscode: string | null  // Passcode encrypted with API key (for owner retrieval)
  createdVia: 'api' | 'mcp' | 'ui'
  rawSize: number  // Size in bytes of the original content
  folderPath: string
  tags: string | null
  format: 'md' | 'mdc'  // Content format
}

export interface SharesListResponse {
  shares: ShareSummary[]
}

export interface AgentConnection {
  name: string
  version: string | null
  firstSeenAt: number
  lastSeenAt: number
  requestCount: number
  lastIp: string | null
  lastCountry: string | null
}

export interface AgentsListResponse {
  agents: AgentConnection[]
}

export interface GenerateKeyOptions {
  ttl?: TTLOption
  name?: string
}

/**
 * Generate a new API key
 * Note: Free tier accounts are capped at 90 days expiration on the backend
 */
export async function generateApiKey(options: GenerateKeyOptions = {}): Promise<ApiKeyResponse> {
  return apiRequest<ApiKeyResponse>('/keys', {
    method: 'POST',
    body: {
      ttl: options.ttl || '90d',
      name: options.name || undefined,
    },
  })
}

/**
 * Validate an API key and get stats
 */
export async function validateApiKey(apiKey: string): Promise<ApiKeyStats> {
  return apiRequest<ApiKeyStats>('/keys/validate', {
    method: 'POST',
    headers: {
      'X-Myrkup-API-Key': apiKey,
    },
  })
}

/**
 * Revoke an API key
 */
export async function revokeApiKey(apiKey: string): Promise<{ success: boolean; message: string }> {
  return apiRequest('/keys', {
    method: 'DELETE',
    headers: {
      'X-Myrkup-API-Key': apiKey,
    },
  })
}

/**
 * List all shares linked to an API key
 */
export async function listApiKeyShares(apiKey: string): Promise<SharesListResponse> {
  return apiRequest<SharesListResponse>('/keys/shares', {
    method: 'GET',
    headers: {
      'X-Myrkup-API-Key': apiKey,
    },
  })
}

/**
 * Revoke a share using API key
 */
export async function revokeShareWithApiKey(shareId: string, apiKey: string): Promise<void> {
  await apiRequest(`/shares/${shareId}`, {
    method: 'DELETE',
    headers: {
      'X-Myrkup-API-Key': apiKey,
    },
  })
}

/**
 * Update API key name
 */
export async function updateApiKeyName(apiKey: string, name: string | null): Promise<{ success: boolean; name: string | null }> {
  return apiRequest('/keys', {
    method: 'PATCH',
    headers: {
      'X-Myrkup-API-Key': apiKey,
    },
    body: { name },
  })
}

/**
 * Update API key expiration
 */
export async function updateApiKeyExpiration(apiKey: string, ttl: TTLOption): Promise<{ success: boolean; expiresAt: number | null }> {
  return apiRequest('/keys', {
    method: 'PATCH',
    headers: {
      'X-Myrkup-API-Key': apiKey,
    },
    body: { ttl },
  })
}

/**
 * Update API key tier (demo only - no payment)
 */
export async function updateApiKeyTier(apiKey: string, tier: 'free' | 'starter' | 'pro'): Promise<{ success: boolean; tier: 'free' | 'starter' | 'pro'; message: string }> {
  return apiRequest('/keys/tier', {
    method: 'PATCH',
    headers: {
      'X-Myrkup-API-Key': apiKey,
    },
    body: { tier },
  })
}

/**
 * List all MCP agents connected to an API key
 */
export async function listApiKeyAgents(apiKey: string): Promise<AgentsListResponse> {
  return apiRequest<AgentsListResponse>('/keys/agents', {
    method: 'GET',
    headers: {
      'X-Myrkup-API-Key': apiKey,
    },
  })
}
